import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, MagnifyingGlass, FileXls,
  Wrench, WarningCircle, Camera, UploadSimple, X,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import ProjectBadge from "@/components/ProjectBadge";
import ProjectField, { assertProject, defaultProject } from "@/components/ProjectField";
import { compressImage } from "@/lib/imageCompress";
import EquipmentPicker, { reloadEquipmentCache } from "@/components/EquipmentPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

/**
 * Backlog de Inspecciones Diarias — reproduce el layout del Excel
 * `Formato_Backlog_Inspecciones_Diarias.xlsx` con los mismos 25 campos y
 * las mismas listas de validacion. Sirve como captura diaria del turno.
 */

const CRIT_COLOR = {
  A: "bg-red-600 text-white",
  B: "bg-amber-500 text-white",
  C: "bg-slate-500 text-white",
};
const CRIT_LABEL = {
  A: "A · <24h",
  B: "B · <72h",
  C: "C · Próx. ventana",
};

const emptyForm = () => ({
  fecha_inspeccion: new Date().toISOString().slice(0, 10),
  project_id: defaultProject(),
  turno: "1ro",
  inspector: "",
  area_linea: "",
  equipo_maquina: "",
  tag_activo: "",
  origen: "Checklist Diario",
  tipo_hallazgo: "Fuga",
  descripcion: "",
  foto_url: "",
  criticidad: "B",
  riesgo: "",
  genera_ot: false,
  ot_folio: "",
  estado_ot: "",
  tipo_trabajo: "Correctivo",
  horas_estimadas: 2,
  refaccion: "",
  requiere_paro: false,
  fecha_programada: "",
  responsable: "",
  fecha_cierre: "",
  horas_reales: 0,
  comentario_cierre: "",
});

export default function InspectionsTab({ canWrite }) {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [equipList, setEquipList] = useState([]);      // maint_equipment para autocomplete
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [criticity, setCriticity] = useState("all");
  const [estado, setEstado] = useState("all");
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [importBusy, setImportBusy] = useState(false);
  const importRef = useRef(null);

  useEffect(() => {
    api.get("/maintenance/inspections/catalog")
      .then((r) => setCatalog(r.data))
      .catch(() => setCatalog(null));
    // Cargamos la lista de equipos del catalogo -- alimenta el autocomplete
    api.get("/maintenance/equipment")
      .then((r) => setEquipList(r.data || []))
      .catch(() => setEquipList([]));
  }, []);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams({ start, end });
    if (criticity !== "all") params.set("criticidad", criticity);
    if (estado !== "all") params.set("estado_ot", estado);
    api.get(`/maintenance/inspections?${params.toString()}`)
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [start, end, criticity, estado]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const term = q.trim().toLowerCase();
    return items.filter((it) => (
      (it.inspection_number || "").toLowerCase().includes(term) ||
      (it.equipo_maquina || "").toLowerCase().includes(term) ||
      (it.tag_activo || "").toLowerCase().includes(term) ||
      (it.descripcion || "").toLowerCase().includes(term) ||
      (it.responsable || "").toLowerCase().includes(term) ||
      (it.inspector || "").toLowerCase().includes(term)
    ));
  }, [items, q]);

  const stats = useMemo(() => {
    const s = { total: items.length, A: 0, B: 0, C: 0, breach: 0, closed: 0, open_ot: 0 };
    items.forEach((it) => {
      if (it.criticidad in s) s[it.criticidad] += 1;
      if (it.sla_breach) s.breach += 1;
      if (it.estado_ot === "CERRADA") s.closed += 1;
      if (it.genera_ot && it.estado_ot !== "CERRADA") s.open_ot += 1;
    });
    return s;
  }, [items]);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDlgOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...emptyForm(),
      ...Object.fromEntries(Object.keys(emptyForm()).map((k) => [k, it[k] ?? emptyForm()[k]])),
    });
    setDlgOpen(true);
  };

  const save = async () => {
    if (!form.equipo_maquina.trim() || !form.descripcion.trim()) {
      toast.error("Equipo y descripcion son requeridos");
      return;
    }
    const payload = {
      ...form,
      horas_estimadas: Number(form.horas_estimadas) || 0,
      horas_reales: Number(form.horas_reales) || 0,
    };
    if (!assertProject(form.project_id)) return;
    try {
      if (editing) {
        await api.put(`/maintenance/inspections/${editing.id}`, payload);
        toast.success(`${editing.inspection_number} actualizada`);
      } else {
        const { data } = await api.post("/maintenance/inspections", payload);
        toast.success(`${data.inspection_number} creada`);
      }
      setDlgOpen(false); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Borrar la inspeccion ${it.inspection_number}?`)) return;
    try {
      await api.delete(`/maintenance/inspections/${it.id}`);
      toast.success("Inspeccion borrada"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toOT = async (it) => {
    try {
      const { data } = await api.post(`/maintenance/inspections/${it.id}/to-order`);
      if (data.already_linked) toast.info(`Ya estaba vinculada a ${data.ot_number}`);
      else toast.success(`OT ${data.ot_number} creada desde ${it.inspection_number}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadXlsx = async () => {
    try {
      const r = await api.get(`/maintenance/inspections/export.xlsx?start=${start}&end=${end}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `backlog-inspecciones-${start}_${end}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const importXlsx = async (file) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/maintenance/inspections/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.errors?.length) {
        toast.warning(`Import: ${data.created} creadas, ${data.skipped} duplicadas, ${data.errors.length} errores`);
      } else {
        toast.success(`Import OK: ${data.created} creadas, ${data.skipped} duplicadas`);
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setImportBusy(false); if (importRef.current) importRef.current.value = ""; }
  };

  return (
    <div className="space-y-4" data-testid="mnt-inspections-tab">
      <p className="text-xs text-slate-500">
        Captura diaria de <b>hallazgos de inspeccion</b> por turno. Criticidad
        <b> A</b> se atiende en {"<24h"}, <b>B</b> en {"<72h"}, <b>C</b> en la
        proxima ventana de mantenimiento. Cada hallazgo puede convertirse en OT
        del backlog con un click.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <StatCard label="Total" value={stats.total} testid="mnt-insp-stat-total" />
        <StatCard label="Crit. A" value={stats.A} accent="red" testid="mnt-insp-stat-a" />
        <StatCard label="Crit. B" value={stats.B} accent="amber" testid="mnt-insp-stat-b" />
        <StatCard label="Crit. C" value={stats.C} testid="mnt-insp-stat-c" />
        <StatCard label="SLA vencidos" value={stats.breach} accent="red" testid="mnt-insp-stat-breach" />
        <StatCard label="Cerradas" value={stats.closed} accent="emerald" testid="mnt-insp-stat-closed" />
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="overline-label">Desde</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-9 w-36" data-testid="mnt-insp-start" />
        </div>
        <div><Label className="overline-label">Hasta</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-9 w-36" data-testid="mnt-insp-end" />
        </div>
        <div><Label className="overline-label">Criticidad</Label>
          <Select value={criticity} onValueChange={setCriticity}>
            <SelectTrigger className="h-9 w-32" data-testid="mnt-insp-crit"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="A">A</SelectItem>
              <SelectItem value="B">B</SelectItem>
              <SelectItem value="C">C</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="overline-label">Estado OT</Label>
          <Select value={estado} onValueChange={setEstado}>
            <SelectTrigger className="h-9 w-44" data-testid="mnt-insp-estado"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Todos</SelectItem>
              {(catalog?.estado_ot || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="relative">
          <Label className="overline-label">Buscar</Label>
          <MagnifyingGlass size={13} className="absolute left-2 bottom-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ID, equipo, TAG, descripcion..." className="h-9 w-72 pl-7" data-testid="mnt-insp-search" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" className="gap-1.5 border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={downloadXlsx} data-testid="mnt-insp-export">
            <FileXls size={14} weight="bold" /> Exportar Excel
          </Button>
          {canWrite && (
            <>
              <input ref={importRef} type="file" accept=".xlsx,.xlsm" className="hidden"
                onChange={(e) => importXlsx(e.target.files?.[0])} data-testid="mnt-insp-import-input" />
              <Button variant="outline" size="sm" className="gap-1.5 border-slate-300 hover:bg-slate-50"
                onClick={() => importRef.current?.click()} disabled={importBusy} data-testid="mnt-insp-import-btn">
                <UploadSimple size={14} weight="bold" /> {importBusy ? "Importando..." : "Importar Excel"}
              </Button>
              <Button onClick={openNew} className="bg-blue-900 hover:bg-blue-800 gap-2" data-testid="mnt-insp-new">
                <Plus size={14} weight="bold" /> Nueva inspeccion
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <p className="text-sm text-slate-400 py-10 text-center">Cargando...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 py-10 text-center">Sin inspecciones en el rango.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="mnt-insp-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500 whitespace-nowrap">
                <tr>
                  <th className="text-left px-2 py-2">ID</th>
                  <th className="text-left px-2 py-2">Fecha · Turno</th>
                  <th className="text-left px-2 py-2">Equipo · TAG</th>
                  <th className="text-left px-2 py-2">Tipo</th>
                  <th className="text-left px-2 py-2 min-w-[240px]">Descripcion</th>
                  <th className="text-center px-2 py-2">Crit.</th>
                  <th className="text-center px-2 py-2">SLA</th>
                  <th className="text-center px-2 py-2">Estado OT</th>
                  <th className="text-left px-2 py-2">Responsable</th>
                  {canWrite && <th></th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((it) => (
                  <tr key={it.id} data-testid={`mnt-insp-row-${it.inspection_number}`} className="hover:bg-slate-50">
                    <td className="px-2 py-2 font-mono font-bold text-blue-900">{it.inspection_number}<br /><ProjectBadge id={it.project_id} /></td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <p className="font-semibold text-slate-800">{it.fecha_inspeccion}</p>
                      <p className="text-[10px] text-slate-500">Turno {it.turno} · {it.origen}</p>
                    </td>
                    <td className="px-2 py-2">
                      <p className="font-semibold text-slate-800">{it.equipo_maquina}</p>
                      <p className="text-[10px] text-slate-500">{it.tag_activo || "—"} · {it.area_linea}</p>
                    </td>
                    <td className="px-2 py-2">
                      <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-800">{it.tipo_hallazgo}</span>
                    </td>
                    <td className="px-2 py-2">
                      <p className="text-slate-800 line-clamp-2 max-w-[280px]">{it.descripcion}</p>
                      {it.refaccion && <p className="text-[10px] text-slate-500 mt-0.5"><Wrench size={9} className="inline mr-0.5" /> {it.refaccion}</p>}
                    </td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-block text-[10px] font-black px-2 py-0.5 rounded ${CRIT_COLOR[it.criticidad] || "bg-slate-200"}`} title={CRIT_LABEL[it.criticidad]}>{it.criticidad}</span>
                    </td>
                    <td className="px-2 py-2 text-center">
                      {it.sla_breach ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-700" data-testid={`mnt-insp-breach-${it.inspection_number}`}>
                          <WarningCircle size={11} weight="fill" /> {Math.round(it.age_hours)}h
                        </span>
                      ) : it.age_hours != null ? (
                        <span className="text-[10px] text-slate-500">{Math.round(it.age_hours)}h</span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center whitespace-nowrap">
                      {it.genera_ot ? (
                        <div>
                          <p className="font-mono text-[10px] font-bold text-blue-800">{it.ot_folio || "—"}</p>
                          <p className="text-[10px] text-slate-500">{it.estado_ot || "PROGRAMADA"}</p>
                        </div>
                      ) : (
                        canWrite && (
                          <button onClick={() => toOT(it)} data-testid={`mnt-insp-to-ot-${it.inspection_number}`} className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-orange-500 text-white hover:bg-orange-600" title="Convertir hallazgo en OT del backlog">
                            → OT
                          </button>
                        )
                      )}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      <p className="text-slate-700">{it.responsable || "—"}</p>
                      <p className="text-[10px] text-slate-500">{it.inspector || ""}</p>
                    </td>
                    {canWrite && (
                      <td className="px-2 py-2 text-right whitespace-nowrap">
                        <button onClick={() => openEdit(it)} data-testid={`mnt-insp-edit-${it.inspection_number}`} className="text-slate-400 hover:text-blue-800 mr-2"><PencilSimple size={13} /></button>
                        <button onClick={() => remove(it)} data-testid={`mnt-insp-delete-${it.inspection_number}`} className="text-slate-400 hover:text-red-500"><Trash size={13} /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <InspectionDialog
        open={dlgOpen}
        onOpenChange={setDlgOpen}
        catalog={catalog}
        form={form}
        setForm={setForm}
        editing={editing}
        onSave={save}
      />
    </div>
  );
}

function StatCard({ label, value, accent, testid }) {
  const tones = {
    red: "text-red-700 border-red-300",
    amber: "text-amber-700 border-amber-300",
    emerald: "text-emerald-700 border-emerald-300",
  };
  return (
    <div data-testid={testid} className={`rounded border bg-white p-2.5 ${tones[accent] || "border-slate-200 text-slate-900"}`}>
      <p className="text-[9px] uppercase tracking-widest font-bold text-slate-500">{label}</p>
      <p className="font-heading font-black text-xl">{value}</p>
    </div>
  );
}

function InspectionDialog({ open, onOpenChange, catalog, equipList, form, setForm, editing, onSave, onReloadEditing }) {
  // Hooks MUST be declared before any conditional return (Rules of Hooks).
  // Autocomplete equipo: sugerencias filtradas por prefijo (case insensitive)
  const equipSuggestions = useMemo(() => {
    const q = (form.equipo_maquina || "").trim().toLowerCase();
    if (q.length < 1) return [];
    return (equipList || [])
      .filter((eq) => {
        const label = `${eq.code || ""} ${eq.name || ""}`.toLowerCase();
        return label.includes(q) && `${eq.code} ${eq.name}` !== form.equipo_maquina;
      })
      .slice(0, 6);
  }, [form.equipo_maquina, equipList]);

  if (!catalog) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickEquip = (eq) => {
    setForm((f) => ({
      ...f,
      equipo_maquina: `${eq.code} ${eq.name || ""}`.trim(),
      tag_activo: eq.code || f.tag_activo,
      area_linea: eq.area || f.area_linea,
    }));
  };

  const uploadPhoto = async (fileInput) => {
    const file = fileInput?.files?.[0];
    if (!file || !editing) return;
    try {
      let payload = file;
      try {
        const c = await compressImage(file);
        payload = c.file;
      } catch (_e) { /* fallback original */ }
      const fd = new FormData();
      fd.append("file", payload);
      const { data } = await api.post(`/maintenance/inspections/${editing.id}/photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("foto_url", data.foto_url);
      onReloadEditing?.({ ...editing, foto_url: data.foto_url });
      toast.success(`Foto subida (${Math.round(data.size / 1024)} KB)`);
    } catch (e) { toast.error(apiError(e)); }
    finally { if (fileInput) fileInput.value = ""; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="mnt-insp-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? `Editar ${editing.inspection_number}` : "Nueva inspeccion"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <ProjectField value={form.project_id} onChange={(v) => set("project_id", v)} testid="mnt-insp-f-project" />
          <div><Label className="overline-label">Fecha inspeccion *</Label>
            <Input type="date" value={form.fecha_inspeccion} onChange={(e) => set("fecha_inspeccion", e.target.value)} data-testid="mnt-insp-f-fecha" />
          </div>
          <div><Label className="overline-label">Turno</Label>
            <Select value={form.turno} onValueChange={(v) => set("turno", v)}>
              <SelectTrigger data-testid="mnt-insp-f-turno"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{catalog.turno.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline-label">Inspector</Label>
            <Input value={form.inspector} onChange={(e) => set("inspector", e.target.value)} data-testid="mnt-insp-f-inspector" />
          </div>
          <div><Label className="overline-label">Area / Linea</Label>
            <Input value={form.area_linea} onChange={(e) => set("area_linea", e.target.value)} data-testid="mnt-insp-f-area" />
          </div>
          <div className="relative">
            <Label className="overline-label">Equipo / Maquina *</Label>
            <EquipmentPicker
              mode="label"
              value={form.equipo_maquina}
              onChange={(v, eq) => set("equipo_maquina", v)}
              dataTestId="mnt-insp-f-equipo"
              placeholder="Selecciona del catálogo de equipos…"
            />
          </div>
          <div><Label className="overline-label">TAG / No. Activo</Label>
            <Input value={form.tag_activo} onChange={(e) => set("tag_activo", e.target.value)} data-testid="mnt-insp-f-tag" />
          </div>
          <div><Label className="overline-label">Origen</Label>
            <Select value={form.origen} onValueChange={(v) => set("origen", v)}>
              <SelectTrigger data-testid="mnt-insp-f-origen"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{catalog.origen.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline-label">Tipo hallazgo</Label>
            <Select value={form.tipo_hallazgo} onValueChange={(v) => set("tipo_hallazgo", v)}>
              <SelectTrigger data-testid="mnt-insp-f-tipo"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{catalog.tipo_hallazgo.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="overline-label">Criticidad</Label>
            <Select value={form.criticidad} onValueChange={(v) => set("criticidad", v)}>
              <SelectTrigger data-testid="mnt-insp-f-crit"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {catalog.criticidad.map((v) => <SelectItem key={v} value={v}>{v} · {CRIT_LABEL[v]?.split(" · ")[1] || ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-3">
            <Label className="overline-label">Descripcion del hallazgo (Que, Donde, Como) *</Label>
            <Textarea rows={2} value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} placeholder="Ej: Fuga de aceite hidraulico en manguera superior lado operador, goteo constante" data-testid="mnt-insp-f-desc" />
          </div>
          <div className="col-span-3">
            <Label className="overline-label">Foto / Evidencia</Label>
            <div className="flex items-start gap-3">
              {form.foto_url ? (
                <div className="relative">
                  {form.foto_url.startsWith("data:") ? (
                    <img src={form.foto_url} alt="Evidencia" data-testid="mnt-insp-f-foto-preview" className="w-32 h-32 object-cover rounded border border-slate-300" />
                  ) : (
                    <div className="w-32 h-32 flex items-center justify-center bg-slate-100 rounded border border-slate-300 text-[10px] text-slate-500 text-center px-1 break-all">{form.foto_url}</div>
                  )}
                  <button type="button" onClick={() => set("foto_url", "")} data-testid="mnt-insp-f-foto-clear"
                    className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center hover:bg-red-600">
                    <X size={10} weight="bold" />
                  </button>
                </div>
              ) : (
                <div className="w-32 h-32 flex items-center justify-center bg-slate-50 rounded border border-dashed border-slate-300 text-slate-400">
                  <Camera size={24} />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <Input value={form.foto_url.startsWith("data:") ? "(imagen subida)" : form.foto_url}
                  onChange={(e) => set("foto_url", e.target.value)}
                  placeholder="URL o nombre — o sube un JPG"
                  disabled={form.foto_url.startsWith("data:")}
                  data-testid="mnt-insp-f-foto" />
                {editing ? (
                  <label className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-900 cursor-pointer hover:underline">
                    <Camera size={13} weight="bold" />
                    Subir JPG/PNG
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      onChange={(e) => uploadPhoto(e.target)} data-testid="mnt-insp-f-foto-upload" />
                  </label>
                ) : (
                  <p className="text-[10px] text-slate-400 italic">Crea primero la inspeccion, luego edita para subir foto.</p>
                )}
              </div>
            </div>
          </div>
          <div className="col-span-3">
            <Label className="overline-label">Riesgo asociado</Label>
            <Select value={form.riesgo || "_none"} onValueChange={(v) => set("riesgo", v === "_none" ? "" : v)}>
              <SelectTrigger data-testid="mnt-insp-f-riesgo"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="_none">— sin especificar —</SelectItem>
                {catalog.riesgo.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="col-span-3 border-t pt-3 mt-1">
            <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">Datos de OT (si aplica)</p>
          </div>
          <div className="col-span-3 flex items-center gap-2">
            <Switch checked={form.genera_ot} onCheckedChange={(v) => set("genera_ot", v)} data-testid="mnt-insp-f-ot" />
            <span className="text-sm">Se atiende con OT generada</span>
          </div>
          {form.genera_ot && (
            <>
              <div><Label className="overline-label">No. OT / Folio</Label>
                <OtFolioPicker
                  equipoLabel={form.equipo_maquina}
                  value={form.ot_folio}
                  onChange={(v) => set("ot_folio", v)}
                />
              </div>
              <div><Label className="overline-label">Estado OT</Label>
                <Select value={form.estado_ot || "_none"} onValueChange={(v) => set("estado_ot", v === "_none" ? "" : v)}>
                  <SelectTrigger data-testid="mnt-insp-f-estado"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="_none">—</SelectItem>
                    {catalog.estado_ot.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="overline-label">Tipo trabajo</Label>
                <Select value={form.tipo_trabajo} onValueChange={(v) => set("tipo_trabajo", v)}>
                  <SelectTrigger data-testid="mnt-insp-f-tipotrab"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{catalog.tipo_trabajo.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="overline-label">Horas estimadas</Label>
                <Input type="number" step="0.5" min="0" value={form.horas_estimadas} onChange={(e) => set("horas_estimadas", e.target.value)} data-testid="mnt-insp-f-hest" />
              </div>
              <div className="col-span-2"><Label className="overline-label">Refaccion requerida</Label>
                <Input value={form.refaccion} onChange={(e) => set("refaccion", e.target.value)} data-testid="mnt-insp-f-refac" />
              </div>
              <div className="col-span-1 flex items-end gap-2">
                <label className="flex items-center gap-2 pb-1.5 cursor-pointer">
                  <Switch checked={form.requiere_paro} onCheckedChange={(v) => set("requiere_paro", v)} data-testid="mnt-insp-f-paro" />
                  <span className="text-xs">Requiere paro</span>
                </label>
              </div>
              <div><Label className="overline-label">Fecha programada</Label>
                <Input type="date" value={form.fecha_programada} onChange={(e) => set("fecha_programada", e.target.value)} data-testid="mnt-insp-f-fprog" />
              </div>
              <div><Label className="overline-label">Responsable</Label>
                <Input value={form.responsable} onChange={(e) => set("responsable", e.target.value)} data-testid="mnt-insp-f-resp" />
              </div>
              <div><Label className="overline-label">Fecha cierre</Label>
                <Input type="date" value={form.fecha_cierre} onChange={(e) => set("fecha_cierre", e.target.value)} data-testid="mnt-insp-f-fcierre" />
              </div>
              <div><Label className="overline-label">Horas reales</Label>
                <Input type="number" step="0.5" min="0" value={form.horas_reales} onChange={(e) => set("horas_reales", e.target.value)} data-testid="mnt-insp-f-hreal" />
              </div>
              <div className="col-span-3">
                <Label className="overline-label">Comentario cierre</Label>
                <Textarea rows={2} value={form.comentario_cierre} onChange={(e) => set("comentario_cierre", e.target.value)} placeholder="Notas del cierre, causa raiz, etc." data-testid="mnt-insp-f-comentario" />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={onSave} className="bg-blue-900 hover:bg-blue-800" data-testid="mnt-insp-save">
            {editing ? "Guardar cambios" : "Crear inspeccion"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * OtFolioPicker · Dropdown de OTs abiertas del equipo elegido en la inspección.
 * Extrae el código del equipo del label "CODE · Name" y consulta al backend.
 * Si no hay OTs abiertas, deja escribir manualmente el folio (fallback).
 */
function OtFolioPicker({ equipoLabel, value, onChange }) {
  const [ots, setOts] = useState([]);
  const [manual, setManual] = useState(false);
  const code = (equipoLabel || "").split(/[·\s]/)[0].trim().toUpperCase();
  useEffect(() => {
    if (!code) { setOts([]); return; }
    api.get(`/maintenance/equipment/by-code/${encodeURIComponent(code)}/open-ots`)
      .then((r) => setOts(r.data || []))
      .catch(() => setOts([]));
  }, [code]);
  if (!code) {
    return (
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid="mnt-insp-f-otfolio"
        placeholder="Selecciona primero el equipo…"
        disabled
      />
    );
  }
  if (manual || ots.length === 0) {
    return (
      <div className="flex gap-1">
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          data-testid="mnt-insp-f-otfolio"
          placeholder={ots.length === 0 ? "Sin OTs abiertas · escribe folio" : "Folio manual"}
        />
        {ots.length > 0 && (
          <button
            type="button"
            onClick={() => setManual(false)}
            className="text-[10px] text-blue-800 hover:underline whitespace-nowrap px-1"
          >
            ← Elegir
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="flex gap-1">
      <Select value={value || "_none"} onValueChange={(v) => onChange(v === "_none" ? "" : v)}>
        <SelectTrigger data-testid="mnt-insp-f-otfolio">
          <SelectValue placeholder="Elige OT abierta…" />
        </SelectTrigger>
        <SelectContent className="bg-white max-h-72 overflow-y-auto">
          <SelectItem value="_none">— sin vinculación —</SelectItem>
          {ots.map((o) => (
            <SelectItem key={o.id} value={o.ot_number}>
              <span className="font-mono font-bold">{o.ot_number}</span>
              <span className="text-slate-500 ml-2">{o.date} · {o.status}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="button"
        onClick={() => setManual(true)}
        className="text-[10px] text-blue-800 hover:underline whitespace-nowrap px-1"
        title="Escribir folio manual"
      >
        Manual
      </button>
    </div>
  );
}
